import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

function StarRating({ rating, size = 18, interactive = false, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1,2,3,4,5].map(star => (
        <span key={star}
          onClick={() => interactive && onChange && onChange(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          style={{ fontSize: size, cursor: interactive ? 'pointer' : 'default', color: star <= (hover || rating) ? '#f5c400' : 'var(--border)', transition: 'color 0.1s' }}>★</span>
      ))}
    </div>
  );
}

export default function Reviews() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: '', content: '' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    api.get('/reviews').then(({ data }) => setReviews(data.reviews || [])).finally(() => setLoading(false));
    api.get('/platforms').then(({ data }) => setPlatforms(data.platforms || []));
  }, []);

  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/reviews', form);
      setMessage({ type: 'success', text: '✅ Review submitted! It will appear after admin approval.' });
      setShowForm(false);
      setForm({ rating: 5, title: '', content: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Error submitting review' });
    } finally { setSubmitting(false); }
    setTimeout(() => setMessage(null), 5000);
  };

  const ratingCounts = [5,4,3,2,1].map(r => ({
    star: r,
    count: reviews.filter(rv => rv.rating === r).length,
    pct: reviews.length ? Math.round((reviews.filter(rv => rv.rating === r).length / reviews.length) * 100) : 0
  }));

  return (
    <div style={{ paddingTop: 70 }}>
      <div className="page-header">
        <h1>REVIEWS</h1>
        <p>What our players are saying</p>
      </div>

      <div className="container" style={{ paddingBottom: 80 }}>
        {message && (
          <div style={{ background: message.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${message.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: 10, padding: '12px 16px', color: message.type === 'success' ? '#10b981' : '#ef4444', marginBottom: 24, fontSize: 14 }}>{message.text}</div>
        )}

        {/* Stats + CTA */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 40 }}>
          {/* Rating summary */}
          <div className="card" style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
            <div style={{ textAlign: 'center', flexShrink: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 56, fontWeight: 900, color: '#f5c400', lineHeight: 1 }}>{avgRating}</div>
              <StarRating rating={Math.round(avgRating)} size={20} />
              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{reviews.length} reviews</div>
            </div>
            <div style={{ flex: 1 }}>
              {ratingCounts.map(({ star, count, pct }) => (
                <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', width: 10 }}>{star}</span>
                  <span style={{ color: '#f5c400', fontSize: 12 }}>★</span>
                  <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'var(--gradient-primary)', borderRadius: 3, transition: 'width 0.5s' }} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 24 }}>{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Leave review CTA */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 14 }}>
            <div style={{ fontSize: 40 }}>✍️</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--text-primary)' }}>Share Your Experience</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Your review helps other players find the best games</p>
            {user ? (
              <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>{showForm ? '✕ Cancel' : '⭐ Write a Review'}</button>
            ) : (
              <Link to="/login" className="btn btn-primary">Login to Review</Link>
            )}
          </div>
        </div>

        {/* Review Form */}
        {showForm && (
          <div className="card fade-in" style={{ marginBottom: 32, padding: 32 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 20, color: 'var(--accent-cyan)' }}>WRITE YOUR REVIEW</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Your Rating *</label>
                <StarRating rating={form.rating} size={32} interactive onChange={(r) => setForm({ ...form, rating: r })} />
              </div>
              <div className="form-group">
                <label>Platform (Optional)</label>
                <select value={form.platform || ''} onChange={e => setForm({ ...form, platform: e.target.value })}>
                  <option value="">General Review</option>
                  {platforms.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Title</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Summarize your experience..." />
              </div>
              <div className="form-group">
                <label>Review *</label>
                <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} placeholder="Tell others about your experience with Apex Gaming..." required style={{ resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="btn btn-primary" disabled={submitting} style={{ flex: 1 }}>{submitting ? 'Submitting...' : 'Submit Review'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Reviews List */}
        {loading ? <div className="loading-spinner"><div className="spinner" /></div> :
          reviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⭐</div>
              <p style={{ fontSize: 18 }}>No reviews yet — be the first!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {reviews.map(r => (
                <div key={r._id} className="card fade-in">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: '#0a0900' }}>
                        {r.user?.username?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>{r.user?.username}</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
                          <StarRating rating={r.rating} size={14} />
                          {r.platform && <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-secondary)', padding: '1px 8px', borderRadius: 6 }}>{r.platform.name}</span>}
                        </div>
                      </div>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(r.createdAt).toLocaleDateString()}</span>
                  </div>
                  {r.title && <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 15, marginBottom: 8, color: 'var(--text-primary)' }}>{r.title}</h4>}
                  <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.7 }}>{r.content}</p>
                </div>
              ))}
            </div>
          )
        }
      </div>
    </div>
  );
}
